// tempfix: for stopping eslint to change nth-of-type(1) to nth-of-type (1), with additional spaces

import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { PropsWithZooplaTheme } from '@blueprint/theme';
import { Text } from '@blueprint/text';
import { Link } from '@blueprint/link';
import { ButtonLink } from '@blueprint/button';
import { Heading1 } from '@blueprint/heading';
import { ButtonLinkWithIcon } from '@blueprint/ui';

const MobileMenuWrapper = styled('div')(
    (props) => css`
        background-color: ${props.theme.colors['graphic-background']};
        position: fixed;
        display: block;
        transform: translate(-100%, 0);
        transition: transform 400ms ease-in-out;
        width: 100%;
        height: calc(100vh - 53px);
        z-index: 1;
        overflow: auto;

        ${props.theme.mediaQueries.large} {
            position: relative;
            top: auto;
            transform: translate(0);
            width: 100%;
            height: auto;
            overflow: unset;
        }
    `
);

const ResetLinkBase = (props: PropsWithZooplaTheme) => `
a& {
    color: ${props.theme.colors['primary-dark']};
    font-weight: 600;

    &:visited {
        text-decoration: none;
        color: ${props.theme.colors['primary-dark']};
    }
}`;

// TODO: Remove when we don't need the build for Mason anymore
// we want to pass data-ajax="false" to all our links, so we are using an HOC
type WithAjaxProp = {
    'data-ajax': 'false' | 'true';
};

const withAjaxFalse =
    <P,>(WrappedComponent: React.ComponentType<P & WithAjaxProp>) =>
    (props: P) => {
        return <WrappedComponent data-ajax="false" {...props} />;
    };

const ResetLink = styled(withAjaxFalse(Link))((props) => ResetLinkBase(props));
const ResetButtonLink = styled(withAjaxFalse(ButtonLink))((props) =>
    ResetLinkBase(props)
);
const ResetButtonLinkWithIcon = styled(withAjaxFalse(ButtonLinkWithIcon))(
    (props) => ResetLinkBase(props)
);

const SkipToContentLink = styled(ResetLink)(
    (props) => css`
        position: absolute;
        background: ${props.theme.colors['primary-brand']};
        font-size: ${props.theme.type.body};
        border-radius: ${props.theme.borderRadius['4px']};
        width: 1px;
        height: 1px;
        margin: -1px;
        clip-path: inset(100%);
        clip: rect(0 0 0 0);
        overflow: hidden;

        &:active,
        &:focus {
            top: ${props.theme.spacing['8px']};
            left: ${props.theme.spacing['16px']};
            display: block;
            width: auto;
            height: auto;
            margin: 0;
            clip: auto;
            overflow: visible;
            padding: ${props.theme.spacing['8px']};
            clip-path: none;
            z-index: 3;
        }

        a& {
            color: ${props.theme.colors['white']};

            &:visited {
                color: ${props.theme.colors['white']};
                text-decoration: none;
            }
        }
    `
);

const MaxWidthContainer = styled('div')(
    (props) => css`
        position: relative;
        display: block;
        width: 100%;
        max-width: 100vw;
        margin: 0 auto;
        overflow: hidden;
        height: 53px;
        will-change: height;
        transition: height 0ms ease-in-out;
        transition-delay: 400ms;

        ${props.theme.mediaQueries.large} {
            height: auto;
            overflow: visible;
        }
    `
);

const MainHeader = styled('div')<{ noShadow?: boolean }>(
    (props) => css`
        position: absolute;
        top: 0;
        left: 0;
        display: flex;
        flex-flow: column nowrap;
        cursor: auto;
        width: 100%;
        max-width: 100vw;
        overflow: hidden;
        // need the !important because old stacks reset it with !important;
        // to move to the new ZDS layout we also need to start peeling back
        // box-shadow
        ${!props.noShadow &&
        'box-shadow: 0px 0px 8px rgba(50, 39, 68, 0.04),0px 0px 1px rgba(50, 39, 68, 0.32),0px 8px 8px -8px rgba(50, 39, 68, 0.16) !important;'}

        &.is-resizing * {
            animation: none !important;
            transition: none !important;
        }

        ${props.theme.mediaQueries.large} {
            position: relative;
            top: auto;
            left: auto;
            flex-flow: row wrap;
            overflow: visible;
            box-shadow: none !important;
        }

        &[data-universal-header-mobile-menu-open] {
            ${props.theme.mediaQueries.large} {
                overflow-y: visible;
            }

            ${MaxWidthContainer} {
                width: 100%;
                overflow-y: auto;
                height: 100vh;
                transition: height 0ms;
                transition-delay: 0ms;

                ${MobileMenuWrapper} {
                    position: relative;
                    transform: translate(0);
                }
            }
        }
    `
);

const HeaderContainer = styled('div')(
    (props) => css`
        position: relative;
        padding-top: ${props.theme.spacing['64px']};
        background-color: ${props.theme.colors['graphic-background']};

        ${props.theme.mediaQueries.large} {
            padding-top: 0;
        }
    `
);

const VisuallyHiddenBase = (props: PropsWithZooplaTheme) => `
    position: absolute;
    border: 0;

    &:active, &:focus {
        z-index: 10;
        display: block;
        top: 1rem;
        left: 1rem;
        width: auto;
        height: auto;
        margin: 0;
        clip: auto;
        overflow: visible;
        padding: 1rem;
        background-color: ${props.theme.colors['neutral-2']};
    }

    &:not(:focus):not(:active) {
        width: 1px;
        height: 1px;
        margin: -1px;
        padding: 0;
        -webkit-clip-path: inset(100%);
        clip-path: inset(100%);
        clip: rect(0 0 0 0);
        overflow: hidden;
    }
`;
const HiddenSpan = styled(Text)((props) => VisuallyHiddenBase(props));
const HiddenHeading = styled(Heading1)((props) => VisuallyHiddenBase(props));

export {
    HeaderContainer,
    SkipToContentLink,
    MainHeader,
    HiddenSpan,
    HiddenHeading,
    MobileMenuWrapper,
    MaxWidthContainer,
    ResetLink,
    ResetButtonLink,
    ResetButtonLinkWithIcon
};
